import React, { useState, useCallback, useEffect, lazy, Suspense, startTransition } from 'react';
import './App.css';
import TopNav from './components/TopNav';
import {
  Login,
  FirstLoginPasswordReset,
  ResetPassword,
  FormChoice,
  AnesthesiaForm,
  ImplantForm,
  Success,
  Emergency,
  MainScreen,
  DentoalveolarSurgeryForm,
  PracticeSetup,
  NeedleStickInjuryForm,
  PatientInfoForm,
  ComplicationsMenu,
  OperationsMenu,
  ComplaintsMenu,
  SchedulingMenu,
  EquipmentForm,
  ComplaintForm,
  SchedulingForm,
  AggressivePatientForm,
  ItBreachMenu,
  HipaaMenu,
  ItBreachForm,
  HipaaForm,
  ProcedureSelection,
  ProcedurePatientEntry,
  ProcedureTimer
} from './components';
import { doc, addDoc, collection, setDoc, getDoc, updateDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db, storage } from './firebase/firebaseConfig.js';
import { getAuth, signOut, onAuthStateChanged } from 'firebase/auth';
import { signUp } from './firebase/auth';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { queueEmail } from './utils/emailQueue';
import { getEmailTemplate } from './utils/emailTemplates';
import { testEmailQueue, testDirectMail } from './utils/emailTest';
// Security utilities
import { encryptFormData, encryptValue, PHI_FIELDS } from './utils/encryption';
import { logPHIAccess, logFormSubmission, logLogin, logLogout, AUDIT_ACTIONS } from './utils/auditLog';
import { getCurrentOrganization } from './utils/organization';
import { toUserMessage } from './utils/userError';

// DICOM viewer (in-house engine): off by default. Enable on the `testing` branch or locally via
// `.env.local`: REACT_APP_ENABLE_DICOM_VIEWER=true
const ENABLE_DICOM_VIEWER = process.env.REACT_APP_ENABLE_DICOM_VIEWER === 'true';

// Lazy load only the heaviest components to reduce initial bundle size
const DataView = lazy(() => import('./components/dataView'));
const Settings = lazy(() => import('./components/settings'));
const DicomViewer = lazy(() => import('./components/dicom/DicomViewer'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="text-center">
      <div className="inline-block size-8 animate-spin rounded-full border-4 border-solid border-indigo-600 border-r-transparent"></div>
      <p className="mt-4 text-sm text-gray-600">Loading...</p>
    </div>
  </div>
);

const initialAnesthesiaData = {
  sex: '',
  age: '',
  weight: '',
  dateOfSurgery: '',
  americanSocietyAnesthesiologistsClassification: '',
  anesthesiaType: '',
  anesthesiaProvider: '',
  surgicalTime: '',
  primaryProcedure: '',
  coMorbidities: '',
  intraPostOperativeEvents: '',
  intraPostCriticalEvents: '',
  intraPostAirwayPulmonaryEvents: '',
  intraPostCardiacEvents: '',
  intraPostMedicationTransfusionEvents: '',
  intraPostNeurologicalOtherEvents: '',
  intraPostEquipmentEnvironmentEvents: '',
  intraPostProcessEvents: '',
  intraPostOtherEvents: ''
};

const initialImplantData = {
  manufacturer: [],
  itemNumber: '',
  serialNumber: '',
  lotNumber: '',
  insertionDate: '',
  timeToFailure: '', // Single selection (radio button)
  insertionTorque: [],
  contextOfPlacement: [],
  tissueFeatures: [],
  graftingType: [],
  membrane: '',
  membraneType: '',
  healing: [],
  restoringPractitioner: [], 
  prostheticRestoration: '', 
  surgeonIssues: [],
  deviceIssues: [],
  sterilizationMethod: '',
  cyclesBeforeReplacement: '',
  diagnosisAtFailure: [],
  preplacementXray: null,
  removalXray: null
};

const initialDentoalveollarData = {
  jawFracture: '',
  alveolarOsteitis: '',
  sinusExposure: '',
  nerveDamage: '',
  retainedRootTip: '',
  nerveDamageType: '',
  damageAdjacentTeeth: '',
  lipLaceration: '',
  lipBurn: '',
  retainedInstrumentForeignBody: '',
  infection: '',
  hemorrhage: '',
  hemorrhageLocalControl: '',
  hemorrhageEmbolization: '',
  hemorrhageHospitalization: '',
}

const intitialNeedleStickInjuryData = {
  needleStickInjury: ''
}

function App() {
  // Organization/tenant context - determined from subdomain
  const [organization] = useState(() => getCurrentOrganization());
  
  // Parse reset-password link params on mount (user clicks link from email)
  const [resetPasswordParams, setResetPasswordParams] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const path = window.location.pathname;
    const token = p.get('token');
    const oobCode = p.get('oobCode');
    if (path === '/reset-password' && token) {
      return { token };
    }
    if ((path === '/reset-password' || p.get('mode') === 'resetPassword') && oobCode) {
      return { oobCode };
    }
    return null;
  });

  const [step, setStep] = useState('login');
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);
  // Removed update progress screen as it was interfering with auth persistence
  // Auth will be handled normally by onAuthStateChanged
  const [formType, setFormType] = useState('');
  // completionDate is now generated fresh at submission time in handleEmergencySubmit and handleFormSubmission
  const [implantData, setImplantData] = useState(initialImplantData);
  const [anesthesiaData, setAnesthesiaData] = useState(initialAnesthesiaData);
  const [dentoalveolarData, setDentoalveolarData] = useState(initialDentoalveollarData);
  const [needleStickInjuryData, setNeedleStickInjuryData] = useState(intitialNeedleStickInjuryData);
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    practice: '',
    practices: [], // Array of practices user belongs to (for multi-practice support)
    role: '',
    userType: 'Office Staff',
    credentials: {},
    password: '',
    confirmPassword: ''
  });
  
  // Active practice for multi-practice users (stored in localStorage)
  const [activePractice, setActivePractice] = useState(() => {
    return localStorage.getItem('activePractice') || '';
  });
  const [practiceProfile, setPracticeProfile] = useState({
    practice: '',
    officeAddress: '',
    officeCity: '',
    officeState: '',
    officeZipCode: '',
    officePhone: '',
  });
  const [patientInfo, setPatientInfo] = useState({
    patientID: '',
    procedureId: null,
    procedureType: null
  });
  const [userPractice, setUserPractice] = useState('');
  const [pendingToast, setPendingToast] = useState(null); // show toast after redirect
  const [selectedProcedureType, setSelectedProcedureType] = useState('');
  const [procedureData, setProcedureData] = useState({
    procedureId: '',
    procedureType: '',
    patientID: '',
    startTime: null,
    endTime: null,
    duration: null,
    status: 'pending',
    completed: false
  });

  // Initialize auth state listener - wait for Firebase to determine auth state before rendering
  useEffect(() => {
    const auth = getAuth();
    let isInitialCheck = true;
    
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is logged in, fetch their practice info
        const fetchUserPractice = async () => {
          try {
            const userDocRef = doc(db, 'users', user.uid);
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
              const userData = userDocSnap.data();
              
              // Get user's practices (support both old 'practice' and new 'practices' array)
              let userPractices = [];
              if (userData.practices && Array.isArray(userData.practices) && userData.practices.length > 0) {
                userPractices = userData.practices;
              } else if (userData.practice) {
                userPractices = [userData.practice];
              }
              
              // Set the active practice from localStorage or use the first/default practice
              const storedActivePractice = localStorage.getItem('activePractice');
              let currentActivePractice = '';
              if (storedActivePractice && userPractices.includes(storedActivePractice)) {
                currentActivePractice = storedActivePractice;
              } else if (userPractices.length > 0) {
                currentActivePractice = userData.practice || userPractices[0];
              }
              
              setUserPractice(currentActivePractice);
              setActivePractice(currentActivePractice);

              setIsUserLoggedIn(true);
              setUserInfo({
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                email: userData.email || '',
                mobile: userData.mobile || '',
                practice: currentActivePractice,
                practices: userPractices,
                role: userData.role || '',
                userType: userData.userType || 'Office Staff',
                credentials: userData.credentials || {},
                uid: user.uid
              });

              if (userData.requiresPasswordChange) {
                setStep('firstLoginPasswordReset');
              } else {
                setStep('mainScreen');
              }
            }
          } catch (error) {
            console.log("Error fetching user practice:", error);
          }
        };
        fetchUserPractice();
      } else {
        setIsUserLoggedIn(false);
        setStep('login');
      }
      // Mark auth as initialized after first check
      if (isInitialCheck) {
        setAuthInitialized(true);
        isInitialCheck = false;
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!ENABLE_DICOM_VIEWER && step === 'dicomViewer') {
      setStep('mainScreen');
    }
  }, [step]);


  const navigateTo = useCallback((nextStep) => {
    if (nextStep === 'dicomViewer' && !ENABLE_DICOM_VIEWER) {
      setStep('mainScreen');
      return;
    }
    // Wrap lazy-loaded component navigation in startTransition to avoid suspension errors
    if (nextStep === 'viewData' || nextStep === 'settings' || nextStep === 'dicomViewer') {
      startTransition(() => {
        setStep(nextStep);
      });
    } else {
      setStep(nextStep);
    }
  }, []);

  const goBack = useCallback(() => {
    // Navigate back based on current step
    // Operations & Quality menus should go back to mainScreen
    if (step === 'operationsMenu' || step === 'complaintsMenu' || step === 'schedulingMenu' || step === 'complicationsMenuFromProcedure') {
      setStep('mainScreen');
    }
    // Safety & Security menus should go back to mainScreen
    else if (step === 'itBreachMenu' || step === 'hipaaMenu' || step === 'aggressivePatient') {
      setStep('mainScreen');
    }
    // IT breach forms should go back to itBreachMenu
    else if (step === 'dataBreachForm' || step === 'ransomwareForm' || step === 'cyberAttackForm' || 
             step === 'systemBreachForm' || step === 'emailBreachForm' || step === 'deviceBreachForm') {
      setStep('itBreachMenu');
    }
    // HIPAA forms should go back to hipaaMenu
    else if (step === 'unauthorizedDisclosureForm' || step === 'improperAccessForm' || step === 'lostRecordsForm' || 
             step === 'vendorBreachForm' || step === 'patientRightsForm' || step === 'transmissionErrorForm') {
      setStep('hipaaMenu');
    }
    // Forms from Operations & Quality menus - go back to the appropriate menu
    else if (step === 'fillForm') {
      if (formType && formType.startsWith('Complaint')) {
        setStep('complaintsMenu');
      } else if (formType && formType.startsWith('Equipment')) {
        setStep('operationsMenu');
      } else if (formType && formType.startsWith('Scheduling')) {
        setStep('schedulingMenu');
      } else {
        // For other form types, go to main screen
        setStep('mainScreen');
      }
    }
    // Patient info step - check if it's for a form that came from a menu
    else if (step === 'patientInfo') {
      // If formType indicates it came from a menu, go back to that menu
      if (formType && formType.startsWith('Complaint')) {
        setStep('complaintsMenu');
      } else if (formType && formType.startsWith('Equipment')) {
        setStep('operationsMenu');
      } else if (formType && formType.startsWith('Scheduling')) {
        setStep('schedulingMenu');
      } else {
        // For other cases, go to main screen
        setStep('mainScreen');
      }
    }
    // Default: go to main screen
    else {
      setStep('mainScreen');
    }
  }, [step, formType]);

  const goHome = useCallback(() => {
    setStep('mainScreen');
  }, []);

  // Fire any deferred toast when we land on the home screen
  useEffect(() => {
    if (step === 'mainScreen' && pendingToast) {
      // Small delay to ensure the page has rendered
      const timer = setTimeout(() => {
        const { type = 'success', message } = pendingToast;

        if (message) {
          toast[type](message);
        }
        setPendingToast(null);
      }, 300); // Increased delay
      
      return () => clearTimeout(timer);
    }
  }, [step, pendingToast]);

  const handleLogin = useCallback(async () => {
    const auth = getAuth();
    const user = auth.currentUser;
  
    if (user) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          
          // Get user's practices (support both old 'practice' and new 'practices' array)
          let userPractices = [];
          if (userData.practices && Array.isArray(userData.practices) && userData.practices.length > 0) {
            userPractices = userData.practices;
          } else if (userData.practice) {
            userPractices = [userData.practice];
          }
          
          // Set the active practice from localStorage or use the first/default practice
          const storedActivePractice = localStorage.getItem('activePractice');
          let currentActivePractice = '';
          if (storedActivePractice && userPractices.includes(storedActivePractice)) {
            currentActivePractice = storedActivePractice;
          } else if (userPractices.length > 0) {
            currentActivePractice = userData.practice || userPractices[0];
          }
          
          // Set active practice state FIRST
          setUserPractice(currentActivePractice);
          setActivePractice(currentActivePractice);
          localStorage.setItem('activePractice', currentActivePractice);
          
          // Set user info with practices
          setUserInfo({
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            email: userData.email || '',
            mobile: userData.mobile || '',
            practice: currentActivePractice,
            practices: userPractices, // Include practices array for multi-practice selector
            role: userData.role || '',
            userType: userData.userType || 'Office Staff',
            credentials: userData.credentials || {},
            uid: user.uid
          });
          
          // Set logged in state
          setIsUserLoggedIn(true);
          
          // Log successful login for audit
          await logLogin(true, userData.email);
          
          // Navigate to main screen AFTER all state is set
          setStep('mainScreen');
        }
      } catch (error) {
        console.log("Error fetching user info:", error);
        toast.error('Error loading user data. Please try again.');
      }
    }
  }, []);

  const handleFormChoice = useCallback((choice) => {
    setFormType(choice);
    setStep('patientInfo');
  }, []);

  const handleViewData = useCallback(() => {
    navigateTo('viewData');
  }, [navigateTo]);

  const handleLogout = async () => {
    try {
      // Log logout event for audit
      await logLogout();
      
      const auth = getAuth();
      await signOut(auth);
      setStep('login');
    } catch (error) {
      console.error('Logout Error:', error);
    }
  };

  const handleResetPasswordSuccess = useCallback(() => {
    setResetPasswordParams(null);
    window.history.replaceState({}, '', window.location.pathname.replace(/\/reset-password$/, '') || '/');
    setStep('login');
    toast.success('Password set successfully. You can now log in with your new password.');
  }, []);

  const handleResetPasswordExpired = useCallback(() => {
    setResetPasswordParams(null);
    window.history.replaceState({}, '', window.location.pathname.replace(/\/reset-password$/, '') || '/');
    setStep('login');
    toast.error('This reset link has expired. Please request a new one from your administrator.');
  }, []);

  // Handle practice change for multi-practice users
  const handlePracticeChange = useCallback((newPractice) => {
    if (newPractice && newPractice !== activePractice) {
      // Update localStorage
      localStorage.setItem('activePractice', newPractice);
      
      // Update state
      setActivePractice(newPractice);
      setUserPractice(newPractice);
      
      // Update userInfo with new active practice
      setUserInfo(prev => ({
        ...prev,
        practice: newPractice
      }));
      
      // Navigate to main screen to refresh data for new practice
      setStep('mainScreen');
      
      toast.success(`Switched to ${newPractice}`);
    }
  }, [activePractice]);
  
  const handleNeedleStickInjuryChange = useCallback((e) => {
    setNeedleStickInjuryData(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  }, []);

  const handleAnesthesiaChange = useCallback((e) => {
    setAnesthesiaData(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  }, []);

  const handleImplantChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setImplantData(prevState => {
      // Ensure checkbox fields are always arrays
      if (type === 'checkbox') {
        const currentValue = prevState[name];
        // If current value is a string, convert it to an array (only if it's not empty)
        const currentArray = Array.isArray(currentValue) 
          ? currentValue 
          : (currentValue && currentValue !== '' ? [currentValue] : []);
        
        return {
          ...prevState,
          [name]: checked
            ? [...currentArray, value]
            : currentArray.filter(v => v !== value)
        };
      }
      // For non-checkbox inputs, set the value normally
      return {
        ...prevState,
        [name]: value
      };
    });
  }, []);

  const handleDentoalveolarChange = (event) => {
    const { name, type, checked } = event.target;
  
    const value = type === "checkbox" ? (checked ? "Yes" : "No") : event.target.value;
  
    setDentoalveolarData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  

  const handleFileChange = useCallback((event, fieldName) => {
    const file = event.target.files[0];
    if (file) {
      setImplantData(prevData => ({
        ...prevData,
        [fieldName]: file
      }));
    }
  }, []);

  const handleUserInfoChange = useCallback((e) => {
    setUserInfo(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
  }, []);

  const handlePracticeSetup = async (practiceData) => {
    try {
      console.log('Setting up practice:', practiceData);
      alert('Practice profile created successfully.');
    } catch (error) {
      alert(toUserMessage(error, 'Setup failed. Please try again.'));
    }
  };

  // Helper function to update procedure assigned status
  const updateProcedureAssignedStatus = useCallback(async (procedureId) => {
    if (!procedureId) {
      return;
    }

    try {
      // Find the procedure document by procedureId
      const proceduresRef = collection(db, 'Procedures');
      const q = query(proceduresRef, where('procedureId', '==', procedureId));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        // Update the first matching document
        const procedureDoc = querySnapshot.docs[0];
        await updateDoc(procedureDoc.ref, { assigned: true });
      }
    } catch (error) {
      console.error('Error updating procedure assigned status:', error);
      // Don't throw - this is not critical for form submission
    }
  }, []);

  const handleEmergencySubmit = useCallback(async (emergencyData) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        toast.error('No user logged in');
        return;
      }
  
      // Use activePractice from the practice toggle instead of fetching from Firestore
      // This ensures submissions go to the correct practice when user has toggled
      if (!activePractice) {
        toast.error('No active practice selected');
        return;
      }
  
      const formData = {
        ...emergencyData,
        userEmail: user.email.toLowerCase(),
        organization: organization, // Organization/tenant identifier
        practice: activePractice, // Use activePractice from toggle
        submittedAt: new Date().toISOString(),
        patientID: patientInfo?.patientID || '',
        procedureId: patientInfo?.procedureId || null,
        procedureType: patientInfo?.procedureType || null,
        physicianId: patientInfo?.physicianId || null,
        physicianName: patientInfo?.physicianName || null
      };
  
      // Encrypt PHI fields before storing
      const encryptedFormData = await encryptFormData(formData);
  
      const emergencyCollection = 'Emergency';
      const emergencyCollectionRef = collection(db, emergencyCollection);
  
      const docRef = await addDoc(emergencyCollectionRef, encryptedFormData);
      
      // Log PHI access for audit trail
      await logFormSubmission('Emergency', docRef.id, true);
      
      // Queue email notification for emergency using custom template
      try {
        const emailTemplate = getEmailTemplate('Emergency', formData);

        await queueEmail('emergency', formData, {
          subject: emailTemplate.subject,
          text: emailTemplate.text,
          html: emailTemplate.html,
          replyTo: user.email,
          formId: 'emergency'
        });
      } catch (emailError) {
        // Don't fail emergency submission if email fails
        console.error('Error queueing emergency email:', emailError);
      }
      
      // Show toast immediately since we're not navigating away
      toast.success('Emergency report submitted successfully');
      
      // Update procedure assigned status if a procedure was selected
      if (formData.procedureId) {
        await updateProcedureAssignedStatus(formData.procedureId);
      }
      
      // Don't navigate yet - let Emergency component handle navigation after showing treatment options
      // setStep('mainScreen');
    } catch (error) {
      console.error("Error submitting emergency data: ", error);
      toast.error('Emergency submission failed');
    }
  }, [patientInfo, updateProcedureAssignedStatus, activePractice, organization]);

  const handleFormSubmission = useCallback(async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    const payload = e && e.payload ? e.payload : null;
  
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      alert('No user logged in');
      return;
    }
  
    // Use activePractice from the practice toggle instead of fetching from Firestore
    // This ensures submissions go to the correct practice when user has toggled
    if (!activePractice) {
      alert('No active practice selected');
      return;
    }
  
    let data;
    if (formType === 'Anesthesia') {
      data = { ...anesthesiaData };
    } else if (formType === 'Dental Implant') {
      data = { ...implantData };
    } else if (formType === 'Dentoalveolar Surgery') {
      data = { ...dentoalveolarData };
    } else if (formType === 'Needle Stick Injury') {
      data = { ...needleStickInjuryData };
    } else {
      // Operational categories submit their own structured payload
      data = payload || { category: formType };
    }
    
    let parentCollection = 'Complications';
    let parentDocId = 'formRefDoc';
    let subcollectionName = formType; 

    if (e && e.specialCollection === 'AggressivePatientInteractions') {
      parentCollection = 'AggressivePatientInteractions';
      parentDocId = 'root';
      subcollectionName = 'Entries';
    } else if (e && e.specialCollection === 'ITBreach') {
      parentCollection = 'ITBreach';
      parentDocId = 'root';
      // Use subType from payload (preferred) or map from step name
      if (payload && payload.subType) {
        subcollectionName = payload.subType;
      } else {
        const subTypeMapping = {
          'dataBreachForm': 'DataBreach',
          'ransomwareForm': 'RansomwareAttack',
          'cyberAttackForm': 'CyberAttack',
          'systemBreachForm': 'SystemBreach',
          'emailBreachForm': 'EmailBreach',
          'deviceBreachForm': 'DeviceBreach'
        };
        subcollectionName = subTypeMapping[formType] || 'General';
      }
    } else if (e && e.specialCollection === 'HIPAADisclosure') {
      parentCollection = 'HIPAADisclosure';
      parentDocId = 'root';
      // Use subType from payload (preferred) or map from step name
      if (payload && payload.subType) {
        subcollectionName = payload.subType;
      } else {
        const subTypeMapping = {
          'unauthorizedDisclosureForm': 'UnauthorizedDisclosure',
          'improperAccessForm': 'ImproperAccess',
          'lostRecordsForm': 'LostRecords',
          'vendorBreachForm': 'VendorBreach',
          'patientRightsForm': 'PatientRights',
          'transmissionErrorForm': 'TransmissionError'
        };
        subcollectionName = subTypeMapping[formType] || 'General';
      }
    } else if (formType.startsWith('Equipment') || formType.startsWith('Complaint') || formType.startsWith('Scheduling')) {
      parentCollection = 'Operations';
      parentDocId = 'opsRefDoc';
      // Clean up the subcollection name - remove spaces and special characters
      subcollectionName = formType.replace(/\s+/g, '_').replace(/[/-]/g, '_');
    } else {
      // keep defaults for clinical complications
    }

    const extendedData = {
      ...data,
      userEmail: user.email.toLowerCase(),
      organization: organization, // Organization/tenant identifier
      practice: activePractice, // Use activePractice from toggle
      submittedAt: new Date().toISOString(),
      patientID: patientInfo.patientID,
      procedureId: patientInfo.procedureId || null,
      procedureType: patientInfo.procedureType || null,
      physicianId: patientInfo.physicianId || null,
      physicianName: patientInfo.physicianName || null
    };

    const parentDocRef = doc(db, parentCollection, parentDocId);
    const subcollectionRef = collection(parentDocRef, subcollectionName);
  
    try {
      const uploadFile = async (file, path) => {
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        return getDownloadURL(storageRef);
      };
  
      if (formType === 'Dental Implant') {
        if (implantData.preplacementXray) {
          const preplacementXrayURL = await uploadFile(
            implantData.preplacementXray,
            `preplacement_x_rays/${Date.now()}_${implantData.preplacementXray.name}`
          );
          extendedData.preplacementXray = preplacementXrayURL;
        }
        if (implantData.removalXray) {
          const removalXrayURL = await uploadFile(
            implantData.removalXray,
            `removal_x_rays/${Date.now()}_${implantData.removalXray.name}`
          );
          extendedData.removalXray = removalXrayURL;
        }
      }
      
      // Encrypt PHI fields before storing
      const encryptedData = await encryptFormData(extendedData);
  
      const docRef = await addDoc(subcollectionRef, encryptedData);
      
      // Log form submission for audit trail
      await logFormSubmission(formType, docRef.id, true);

      // Queue email notification based on form category with custom templates
      try {
        // Determine category and formId for email routing
        let emailCategory = 'scheduling'; // default
        let emailFormId = null;
        let emailSubCategory = null;
        let specialCollection = e?.specialCollection || null;
        let subType = payload?.subType || subcollectionName;

        // HIPAA Disclosures
        if (specialCollection === 'HIPAADisclosure') {
          emailCategory = 'hipaa';
          const hipaaFormMap = {
            'UnauthorizedDisclosure': 'unauthorizedDisclosure',
            'ImproperAccess': 'improperAccess',
            'LostRecords': 'lostRecords',
            'VendorBreach': 'vendorBreach',
            'PatientRights': 'patientRights',
            'TransmissionError': 'transmissionError'
          };
          emailFormId = hipaaFormMap[subType] || 'unauthorizedDisclosure';
          emailSubCategory = emailFormId;
        } 
        // IT Security Events
        else if (specialCollection === 'ITBreach') {
          emailCategory = 'itBreach';
          const itFormMap = {
            'DataBreach': 'dataEvent',
            'RansomwareAttack': 'ransomwareEvent',
            'CyberAttack': 'cyberEvent',
            'SystemBreach': 'systemEvent',
            'EmailBreach': 'emailEvent',
            'DeviceBreach': 'deviceEvent'
          };
          emailFormId = itFormMap[subType] || 'dataEvent';
          emailSubCategory = emailFormId;
        } 
        // Aggressive Patient Interactions
        else if (specialCollection === 'AggressivePatientInteractions') {
          emailCategory = 'aggressivePatient';
          emailFormId = 'aggressivePatient';
        } 
        // Complications
        else if (parentCollection === 'Complications') {
          emailCategory = 'complications';
          const complicationsFormMap = {
            'Anesthesia': 'anesthesia',
            'Dental Implant': 'dentalImplant',
            'Dentoalveolar Surgery': 'dentoalveolarSurgery',
            'Needle Stick Injury': 'needleStickInjury'
          };
          emailFormId = complicationsFormMap[formType] || 'anesthesia';
        } 
        // Patient Complaints
        else if (formType.startsWith('Complaint')) {
          emailCategory = 'complaints';
          const complaintsFormMap = {
            'Complaint - Clinical Care': 'clinicalCare',
            'Complaint - Customer Service': 'customerService',
            'Complaint - Billing/Financial': 'billingFinancial',
            'Complaint - Facility/Environment': 'facilityEnvironment'
          };
          emailFormId = complaintsFormMap[formType] || 'clinicalCare';
        } 
        // Equipment Malfunction
        else if (formType.startsWith('Equipment')) {
          emailCategory = 'equipment';
          const equipmentFormMap = {
            'Equipment - Surgical': 'surgical',
            'Equipment - Imaging': 'imaging',
            'Equipment - Sterilization': 'sterilization',
            'Equipment - Anesthesia': 'anesthesiaEquipment',
            'Equipment - Computer/Software': 'computerSoftware',
            'Equipment - Facility Systems': 'facilitySystems'
          };
          emailFormId = equipmentFormMap[formType] || 'surgical';
        } 
        // Scheduling/Communication
        else if (formType.startsWith('Scheduling')) {
          emailCategory = 'scheduling';
          const schedulingFormMap = {
            'Scheduling - Appointment': 'appointment',
            'Scheduling - Communication': 'patientCommunication',
            'Scheduling - Documentation': 'documentation',
            'Scheduling - System/Process': 'systemProcess'
          };
          emailFormId = schedulingFormMap[formType] || 'appointment';
        }

        // Generate custom email template based on form type
        const emailTemplate = getEmailTemplate(formType, extendedData, {
          subType,
          specialCollection
        });

        // Queue the email with custom template content
        await queueEmail(emailCategory, extendedData, {
          subject: emailTemplate.subject,
          text: emailTemplate.text,
          html: emailTemplate.html,
          replyTo: user.email,
          ...(emailFormId && { formId: emailFormId }),
          ...(emailSubCategory && { subCategory: emailSubCategory })
        });
      } catch (emailError) {
        // Don't fail form submission if email fails
        console.error('Error queueing email:', emailError);
      }

      setPendingToast({ type: 'success', message: 'Form submitted successfully.' });
      
      // Update procedure assigned status if a procedure was selected
      if (extendedData.procedureId) {
        await updateProcedureAssignedStatus(extendedData.procedureId);
      }
      
      if (formType === 'Anesthesia') {
        setAnesthesiaData(initialAnesthesiaData);
      } else if (formType === 'Dental Implant') {
        setImplantData(initialImplantData);
      } else if (formType === 'Dentoalveolar Surgery') {
        setDentoalveolarData(initialDentoalveollarData);
      } else if (formType === 'Needle Stick Injury') {
        setNeedleStickInjuryData(intitialNeedleStickInjuryData);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(toUserMessage(error, 'Submission failed. Please try again.'));
    }
  
    setStep('mainScreen');
  }, [formType, anesthesiaData, implantData, dentoalveolarData, needleStickInjuryData, patientInfo, updateProcedureAssignedStatus, activePractice, organization]);

  // Generate unique outcome ID
  const generateOutcomeId = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `OUT-${timestamp}-${random}`;
  };

  const handleSuccessDataSubmission = useCallback(async (submissionData) => {
    try {
      const { patientId, practice, userEmail } = submissionData;
      const outcomeId = generateOutcomeId();
      
      // Prepare data with all fields
      const successData = {
        outcomeId,
        userEmail,
        organization: organization, // Organization/tenant identifier
        patientId,
        patientID: patientId, // standardized field
        practice,
        submittedAt: new Date().toISOString(),
        outcomeDescription: submissionData.outcomeDescription || '',
        procedureId: submissionData.procedureId || null,
        procedureType: submissionData.procedureType || null,
        physicianId: submissionData.physicianId || null,
        physicianName: submissionData.physicianName || null
      };
      
      // Encrypt PHI fields before storing
      const encryptedData = await encryptFormData(successData);
  
      const entryRef = collection(db, 'Success', 'successRefDoc', 'Anesthesia');
      await setDoc(doc(entryRef, outcomeId), encryptedData);
      
      // Log form submission for audit trail
      await logFormSubmission('Success', outcomeId, true);
  
      // Update procedure assigned status if a procedure was selected
      if (submissionData.procedureId) {
        await updateProcedureAssignedStatus(submissionData.procedureId);
      }
  
      setPendingToast({ type: 'success', message: 'Success recorded' });
      setStep('mainScreen');
    } catch (error) {
      toast.error('Failed to record success');
    }
  }, [setStep, updateProcedureAssignedStatus, organization]);

  const handleProcedureComplete = useCallback(async (completedProcedureData) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        toast.error('No user logged in');
        return;
      }

      // Use activePractice from the practice toggle instead of fetching from Firestore
      if (!activePractice) {
        toast.error('No active practice selected');
        return;
      }

      const procedureRecord = {
        ...completedProcedureData,
        userEmail: user.email.toLowerCase(),
        practice: activePractice, // Use activePractice from toggle
        completedAt: serverTimestamp(),
        completed: true,
        status: 'completed',
        assigned: false // New procedures start as unassigned
      };
      
      // Encrypt PHI fields before storing
      const encryptedProcedure = await encryptFormData(procedureRecord);

      // Save to Firebase Procedures collection
      const proceduresRef = collection(db, 'Procedures');
      const docRef = await addDoc(proceduresRef, encryptedProcedure);
      
      // Log form submission for audit trail
      await logFormSubmission('Procedures', docRef.id, true);

      toast.success('Procedure completed and saved successfully');
      
      // Reset procedure data
      setProcedureData({
        procedureId: '',
        procedureType: '',
        patientID: '',
        startTime: null,
        endTime: null,
        duration: null,
        status: 'pending',
        completed: false
      });
      setSelectedProcedureType('');
      
      setStep('mainScreen');
    } catch (error) {
      console.error('Error saving procedure:', error);
      toast.error('Failed to save procedure');
    }
  }, [setStep, setProcedureData, setSelectedProcedureType, activePractice]);

  const handleSignup = async (userInfo) => {
    const { firstName, lastName, mobile, role, practice, email, password, confirmPassword} = userInfo;
  
    if (password !== confirmPassword) {
      alert('Passwords do not match.');
      return;
    }
  
    try {
      // Use the signUp function from auth.js which handles both authentication and Firestore document creation
      await signUp(email, password, {
        firstName,
        lastName,
        mobile,
        role,
        practice,
        email: email.toLowerCase()
      });
      alert('Signup successful.');
    } catch (error) {
      alert(toUserMessage(error, 'Signup failed. Please try again.'));
    }
  };

  // Remove initial HTML loader when app is ready
  useEffect(() => {
    if (authInitialized) {
      const loader = document.getElementById('initial-loader');
      if (loader) {
        loader.style.opacity = '0';
        loader.style.transition = 'opacity 0.3s ease-out';
        setTimeout(() => loader.remove(), 300);
      }
    }
  }, [authInitialized]);

  // Don't render login page until auth state is initialized to prevent flash/reload
  if (!authInitialized && step === 'login' && !resetPasswordParams) {
    return null;
  }

  // Reset password page (from email link) - render alone, no TopNav
  if (resetPasswordParams) {
    return (
      <div className="App">
        <ResetPassword
          oobCode={resetPasswordParams.oobCode}
          token={resetPasswordParams.token}
          onSuccess={handleResetPasswordSuccess}
          onExpired={handleResetPasswordExpired}
        />
      </div>
    );
  }

  return (
    <div className="App">
      {step !== 'login' && step !== 'firstLoginPasswordReset' && (
        <TopNav
          onBack={step !== 'mainScreen' ? goBack : null}
          onHome={step !== 'mainScreen' ? goHome : null}
          onSettings={() => navigateTo('settings')}
          onOutcomes={() => navigateTo('viewData')}
          onImaging={ENABLE_DICOM_VIEWER ? () => navigateTo('dicomViewer') : undefined}
          onLogout={handleLogout}
          userPractices={userInfo.practices}
          activePractice={activePractice}
          onPracticeChange={handlePracticeChange}
          organization={organization}
        />
      )}
      

      {step === 'firstLoginPasswordReset' && (
        <FirstLoginPasswordReset
          onComplete={handleLogin}
          onSignOut={() => setStep('login')}
        />
      )}

      {step === 'login' && (
        <Login
          userInfo={userInfo}
          handleUserInfoChange={handleUserInfoChange}
          handleLogin={handleLogin}
          handleSignup={handleSignup}
          practiceProfile={practiceProfile}
          setPracticeProfile={setPracticeProfile}
          handlePracticeSetup={handlePracticeSetup}
          isUserLoggedIn={isUserLoggedIn}
          handleViewData={handleViewData}
          setStep={setStep}
        />
      )}

      {step === 'viewData' && (
        <Suspense fallback={<LoadingFallback />}>
          <DataView practice={activePractice} organization={organization} userRole={userInfo.role} goBack={() => {setStep('login');}} />
        </Suspense>
      )}

      {step === 'mainScreen' && (
        <MainScreen setStep={navigateTo} setPatientInfo={setPatientInfo} activePractice={activePractice} organization={organization} userRole={userInfo.role} />
      )}

      {step === 'chooseForm' && (
        <FormChoice handleFormChoice={handleFormChoice} />
      )}

      {step === 'complicationsMenu' && (
        <ComplicationsMenu
          goBack={() => navigateTo('mainScreen')}
          toOutcomes={() => navigateTo('viewData')}
          toSettings={() => navigateTo('settings')}
          logout={handleLogout}
          onSelect={(choice) => { setFormType(choice); navigateTo('patientInfo'); }}
        />
      )}

      {/* Complications menu accessed from existing procedure - skip patient info */}
      {step === 'complicationsMenuFromProcedure' && (
        <ComplicationsMenu
          goBack={() => navigateTo('mainScreen')}
          toOutcomes={() => navigateTo('viewData')}
          toSettings={() => navigateTo('settings')}
          logout={handleLogout}
          onSelect={(choice) => { setFormType(choice); navigateTo('fillForm'); }}
        />
      )}

      {step === 'operationsMenu' && (
        <OperationsMenu
          goBack={() => navigateTo('mainScreen')}
          toOutcomes={() => navigateTo('viewData')}
          toSettings={() => navigateTo('settings')}
          logout={handleLogout}
          onSelect={(choice) => { setFormType(choice); navigateTo('fillForm'); }}
        />
      )}

      {step === 'complaintsMenu' && (
        <ComplaintsMenu
          goBack={() => navigateTo('mainScreen')}
          toOutcomes={() => navigateTo('viewData')}
          toSettings={() => navigateTo('settings')}
          logout={handleLogout}
          onSelect={(choice) => { setFormType(choice); navigateTo('fillForm'); }}
        />
      )}

      {step === 'schedulingMenu' && (
        <SchedulingMenu
          goBack={() => navigateTo('mainScreen')}
          toOutcomes={() => navigateTo('viewData')}
          toSettings={() => navigateTo('settings')}
          logout={handleLogout}
          onSelect={(choice) => { setFormType(choice); navigateTo('fillForm'); }}
        />
      )}

      {step === 'emergencyPatientInfo' && (
        <PatientInfoForm
          activePractice={activePractice}
          organization={organization}
          onSubmit={(info) => {
            setPatientInfo(info);
            setStep('emergencyCheck');
          }}
        />
      )}

      {step === 'emergencyCheck' && (
        <Emergency handleEmergencySubmit={handleEmergencySubmit} setStep={setStep}/>
      )}

      {step === 'aggressivePatient' && (
        <AggressivePatientForm
          onSubmit={(payload) => handleFormSubmission({ preventDefault: () => {}, payload, specialCollection: 'AggressivePatientInteractions' })}
        />
      )}

      {step === 'fillForm' && formType === 'Anesthesia' && (
        <AnesthesiaForm
          anesthesiaData={{...anesthesiaData}}
          handleAnesthesiaChange={handleAnesthesiaChange}
          handleFormSubmission={handleFormSubmission}
        />
      )}

      {step === 'fillForm' && formType === 'Dental Implant' && (
        <ImplantForm
          implantData={{...implantData}}
          handleImplantChange={handleImplantChange}
          handleFileChange={handleFileChange}
          handleFormSubmission={handleFormSubmission}
        />
      )}

      {step === 'fillForm' && formType === 'Dentoalveolar Surgery' && (
        <DentoalveolarSurgeryForm
          dentoalveolarData={dentoalveolarData}
          handleDentoalveolarChange={handleDentoalveolarChange}
          handleFormSubmission={handleFormSubmission}
        />
      )}

      {step === 'fillForm' && formType === 'Needle Stick Injury' && (
        <NeedleStickInjuryForm
          handleNeedleStickInjuryChange={handleNeedleStickInjuryChange}
          handleFormSubmission={handleFormSubmission}
        />
      )}

      {step === 'fillForm' && formType.startsWith('Equipment') && (
        <EquipmentForm
          category={formType}
          onSubmit={(payload) => handleFormSubmission({ preventDefault: () => {}, payload })}
        />
      )}

      {step === 'fillForm' && formType.startsWith('Complaint') && (
        <ComplaintForm
          category={formType}
          onSubmit={(payload) => handleFormSubmission({ preventDefault: () => {}, payload })}
        />
      )}

      {step === 'fillForm' && formType.startsWith('Scheduling') && (
        <SchedulingForm
          category={formType}
          onSubmit={(payload) => handleFormSubmission({ preventDefault: () => {}, payload })}
        />
      )}

      {step === 'submitData' && (
        <Success 
          handleSuccessDataSubmission={handleSuccessDataSubmission}
          setStep={setStep}
          patientInfo={patientInfo}
        />
      )}

      {step === 'successPatientInfo' && (
        <PatientInfoForm
          activePractice={activePractice}
          organization={organization}
          isSuccessWorkflow={true}
          onSubmit={(info) => {
            setPatientInfo(info);
            // Navigate to success form for outcome description
            setStep('submitData');
          }}
        />
      )}
      
      {step === 'patientInfo' && (
        <PatientInfoForm
          activePractice={activePractice}
          organization={organization}
          onSubmit={(info) => {
            setPatientInfo(info);
            setStep('fillForm');
          }}
        />
      )}

      {step === 'practiceSetup' && (<PracticeSetup/>)}
      {step === 'settings' && (
        <Suspense fallback={<LoadingFallback />}>
          <Settings setStep={setStep} userInfo={userInfo} setUserInfo={setUserInfo} activePractice={activePractice} organization={organization} onPracticeChange={handlePracticeChange} />
        </Suspense>
      )}

      {ENABLE_DICOM_VIEWER && step === 'dicomViewer' && (
        <Suspense fallback={<LoadingFallback />}>
          <DicomViewer />
        </Suspense>
      )}

      {/* IT Breach Menu and Forms */}
      {step === 'itBreachMenu' && (
        <ItBreachMenu setStep={navigateTo} />
      )}

      {(step === 'dataBreachForm' || step === 'ransomwareForm' || step === 'cyberAttackForm' || 
        step === 'systemBreachForm' || step === 'emailBreachForm' || step === 'deviceBreachForm') && (
        <ItBreachForm
          setStep={setStep}
          formType={step}
          onSubmit={(payload) => handleFormSubmission({ preventDefault: () => {}, payload, specialCollection: 'ITBreach' })}
        />
      )}

      {/* HIPAA Menu and Forms */}
      {step === 'hipaaMenu' && (
        <HipaaMenu setStep={navigateTo} />
      )}

      {(step === 'unauthorizedDisclosureForm' || step === 'improperAccessForm' || step === 'lostRecordsForm' || 
        step === 'vendorBreachForm' || step === 'patientRightsForm' || step === 'transmissionErrorForm') && (
        <HipaaForm
          setStep={setStep}
          formType={step}
          onSubmit={(payload) => handleFormSubmission({ preventDefault: () => {}, payload, specialCollection: 'HIPAADisclosure' })}
        />
      )}

      {/* Procedure Components */}
      {step === 'procedureSelection' && (
        <ProcedureSelection 
          setStep={navigateTo} 
          setSelectedProcedureType={setSelectedProcedureType}
        />
      )}

      {step === 'procedurePatientEntry' && (
        <ProcedurePatientEntry 
          setStep={navigateTo}
          selectedProcedureType={selectedProcedureType}
          procedureData={procedureData}
          setProcedureData={setProcedureData}
        />
      )}

      {step === 'procedureTimer' && (
        <ProcedureTimer 
          setStep={navigateTo}
          procedureData={procedureData}
          setProcedureData={setProcedureData}
          activePractice={activePractice}
          userInfo={userInfo}
          onComplete={handleProcedureComplete}
        />
      )}
    </div>
  );
}

// Make email test functions available globally for debugging
if (typeof window !== 'undefined') {
  window.testEmail = testEmailQueue;
  window.testEmailDirect = testDirectMail;
}

export default App;
